import { memo } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Play, Loader2, AlertTriangle } from "lucide-react";
import { useContentStore, getItemMeta, type ContentItem } from "@/stores/contentStore";
import { toast } from "@/hooks/use-toast";

interface ContentCardProps {
  item: ContentItem;
  projectId: number;
  onDragStart: (e: React.DragEvent, itemId: string) => void;
  onDragEnd: () => void;
  onClick: () => void;
}

function AiScoreMeter({ score }: { score: number | undefined }) {
  if (score === undefined) return null;
  const bars = 5;
  const filled = Math.round((score / 100) * bars);
  const color = score <= 20 ? "bg-green-500" : score <= 50 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <span>AI: {score}%</span>
      <div className="flex gap-0.5">
        {Array.from({ length: bars }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "w-1.5 h-2.5 rounded-sm",
              i < filled ? color : "bg-muted"
            )}
          />
        ))}
      </div>
    </div>
  );
}

export const ContentCard = memo(function ContentCard({ item, projectId, onDragStart, onDragEnd, onClick }: ContentCardProps) {
  const displayTitle = item.title || (item.primary_keyword ? `Untitled — ${item.primary_keyword}` : "Untitled");
  const aiScore = item.ai_detection_score ?? undefined;

  return (
    <div
      draggable
      role="button"
      tabIndex={0}
      aria-label={displayTitle}
      onDragStart={(e) => onDragStart(e, item.id)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      className={cn(
        "group/card relative rounded-[6px] border cursor-grab active:cursor-grabbing",
        "transition-all duration-150 p-3 space-y-2",
        "bg-[rgba(17,17,19,0.6)] border-[rgba(255,255,255,0.06)]",
        "hover:border-[rgba(255,255,255,0.12)] hover:bg-[rgba(17,17,19,0.8)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      )}
    >
      <p className="text-sm font-medium leading-tight line-clamp-2">
        {displayTitle}
      </p>

      {item.primary_keyword && (
        <p className="text-xs text-muted-foreground truncate">
          kw: {item.primary_keyword}
        </p>
      )}

      <div className="flex items-center justify-between">
        {item.word_count > 0 && (
          <span className="text-xs text-muted-foreground">
            {item.word_count.toLocaleString()} words
          </span>
        )}
        <AiScoreMeter score={aiScore} />
      </div>

      {item.stage === "reviewing" && (
        <Badge variant="outline" className="text-xs text-amber-400 border-amber-400/30">
          Review needed
        </Badge>
      )}

      {(item.stage === "queued" || item.stage === "scheduled") && (
        <button
          aria-label={`Start pipeline for ${displayTitle}`}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await useContentStore.getState().startPipeline(projectId, item.id);
            } catch {
              toast({ title: "Failed to start pipeline", variant: "destructive" });
            }
          }}
        >
          <Play className="h-3 w-3" /> Start Pipeline
        </button>
      )}

      {(item.stage === "error" || item.stage === "killed") && (
        <div className="mt-2 space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-red-400">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{item.last_error || "Pipeline failed"}</span>
          </div>
          <button
            aria-label={`Restart pipeline for ${displayTitle}`}
            className="flex w-full items-center justify-center gap-1.5 rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await useContentStore.getState().startPipeline(projectId, item.id);
              } catch {
                toast({ title: "Failed to restart pipeline", variant: "destructive" });
              }
            }}
          >
            <Play className="h-3 w-3" /> Restart Pipeline
          </button>
        </div>
      )}

      {["researching", "outlining", "writing"].includes(item.stage) && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-indigo-400">
          <Loader2 className="h-3 w-3 animate-spin" />
          {item.stage === "researching" && "Researching..."}
          {item.stage === "outlining" && "Creating outline..."}
          {item.stage === "writing" && "Writing draft..."}
        </div>
      )}
    </div>
  );
});
